import { EventLogger } from "@websim/core/src/activity/event-logger";
import type { Json } from "@websim/supabase";
let totalDurationLogged = 0;

function isActivityOverlayEnabled() {
  const location = window.location.search;
  const urlParams = new URLSearchParams(location);
  const showActivityOverlay = urlParams.get("__websim_show_activity_overlay");
  return showActivityOverlay === "true";
}

export function initActivityTracking(args: {
  distinctId?: string;
  sessionId?: string;
}) {
  // Logs events to keep track of user activity (e.g. when they're moving their mouse, scrolling, pressing keys.)
  // The events have higher resolution in the beginning.
  // The events are debounced and aggregated into a total active duration. With a max aggregation time of 20s.
  // TODO: on tab closes (and probably other scenarios), the api fetch event won't make it to the parent and wil be lost.

  const projectId = __WEBSIM_DATA__.project_id;
  const projectVersion = __WEBSIM_DATA__.project_version;
  if (!projectId) {
    console.error("No project id found");
    return () => {}; // Return empty cleanup function
  }
  if (!projectVersion) {
    console.error("No project version found");
    return () => {}; // Return empty cleanup function
  }
  const logger = new EventLogger(args);

  const maxSpanTime = 10_000;
  const startTimeMs = performance.now();
  const relativeTime = () => performance.now() - startTimeMs;
  // format: [timeSinceStartMs]: debounceAmountMs
  const debounceTimeSchedule = {
    [60_000]: 2_000,
    [200_000]: 5_000,
    Infinity: 10_000,
  };
  const debounceTime = (t: number) => {
    for (const [key, value] of Object.entries(debounceTimeSchedule)) {
      if (t < Number(key)) {
        return value;
      }
    }
    return -1;
  };

  let currentSpanStart = 0;
  const currentSpanDuration = () => relativeTime() - currentSpanStart;
  let currentTimeout: NodeJS.Timeout | null = null;

  let logContainer: HTMLElement | null = null;
  if (isActivityOverlayEnabled()) {
    logContainer = initActivityOverlay();
  }

  const logSpan = () => {
    const duration = currentSpanDuration();
    totalDurationLogged += duration;
    const attributes = {
      startRelative: Math.round(currentSpanStart),
      duration: Math.round(duration),
      totalDuration: Math.round(totalDurationLogged),
      projectVersion,
    };
    logger.logEvent(
      projectId,
      "site_activity",
      Object.fromEntries(
        Object.entries(attributes).filter(([_, v]) => v !== undefined)
      ) as Record<string, Json>
    );
    if (isActivityOverlayEnabled()) {
      showInactiveActivityOverlay();
      logToActivityOverlay(duration);
    }
  };
  const updateDebounce = () => {
    if (currentTimeout) {
      // Debouncing existing span
      clearTimeout(currentTimeout);
      currentTimeout = setTimeout(() => {
        logSpan();
        currentTimeout = null;
      }, Math.min(debounceTime(relativeTime()), maxSpanTime - currentSpanDuration()));
    } else {
      // Starting new span
      currentSpanStart = relativeTime();
      if (isActivityOverlayEnabled()) {
        showActiveActivityOverlay();
      }
      currentTimeout = setTimeout(() => {
        logSpan();
        currentTimeout = null;
      }, debounceTime(relativeTime()));
    }
  };

  const touchStartHandler = () => {
    updateDebounce();
  };

  const touchMoveHandler = () => {
    updateDebounce();
  };

  const mouseMoveHandler = () => {
    updateDebounce();
  };

  const keyDownHandler = () => {
    updateDebounce();
  };

  const scrollHandler = () => {
    updateDebounce();
  };

  const visibilityChangeHandler = () => {
    if (document.visibilityState === "hidden") {
      if (currentTimeout) {
        // Finish existing span
        clearTimeout(currentTimeout);
        logSpan();
        currentTimeout = null;
        // Flush, in case logger's visibilitychange might trigger first.
        logger.flush();
      }
    }
  };

  window.addEventListener("touchstart", touchStartHandler, { passive: true });
  window.addEventListener("touchmove", touchMoveHandler, { passive: true });
  window.addEventListener("mousemove", mouseMoveHandler, { passive: true });
  window.addEventListener("keydown", keyDownHandler, { passive: true });
  window.addEventListener("scroll", scrollHandler, { passive: true });
  window.addEventListener("visibilitychange", visibilityChangeHandler);

  // Return a cleanup function
  return () => {
    if (currentTimeout) {
      clearTimeout(currentTimeout);
      currentTimeout = null;
    }

    window.removeEventListener("touchstart", touchStartHandler);
    window.removeEventListener("touchmove", touchMoveHandler);
    window.removeEventListener("mousemove", mouseMoveHandler);
    window.removeEventListener("keydown", keyDownHandler);
    window.removeEventListener("scroll", scrollHandler);
    window.removeEventListener("visibilitychange", visibilityChangeHandler);

    // Remove activity overlay if it was created
    if (logContainer && logContainer.parentNode) {
      logContainer.parentNode.removeChild(logContainer);
    }

    // Flush any remaining events
    logger.flush();
  };
}

function showActiveActivityOverlay() {
  document.body.style.opacity = "1";
}

function showInactiveActivityOverlay() {
  document.body.style.opacity = "0.5";
}

function initActivityOverlay(): HTMLElement {
  // Create or get activity log container
  let logContainer = document.getElementById("activity-log-container");
  if (!logContainer) {
    logContainer = document.createElement("div");
    logContainer.id = "activity-log-container";
    logContainer.style.cssText = `
          position: fixed;
          top: 20px;
          right: 20px;
          background: rgba(0, 0, 0, 0.8);
          color: white;
          padding: 12px;
          border-radius: 4px;
          z-index: 9999;
          max-height: 300px;
          min-width: 200px;
          min-height: 100px;
          overflow-y: auto;
          font-family: monospace;
          font-size: 12px;
        `;
    document.body.appendChild(logContainer);
  }
  //initialize with inactive overlay
  showInactiveActivityOverlay();
  return logContainer;
}

function logToActivityOverlay(duration: number) {
  const logContainer = document.getElementById("activity-log-container");
  if (!logContainer) {
    alert("No log container found");
    return;
  }

  // Add new log entry
  const logEntry = document.createElement("div");
  const timestamp = new Date().toLocaleTimeString();
  const durationSec = (duration / 1000).toFixed(2);
  const totalDurationSec = (totalDurationLogged / 1000).toFixed(2);
  logEntry.textContent = `[${timestamp}] Duration: ${durationSec}s | Total: ${totalDurationSec}s`;

  // Alternate background colors based on entry position
  const isEven = logContainer.children.length % 2 === 0;
  logEntry.style.backgroundColor = isEven
    ? "rgba(50, 50, 50, 0.5)"
    : "transparent";
  logEntry.style.borderBottom = "1px solid rgba(255,255,255,0.1)";
  logEntry.style.padding = "4px 0";

  // Add new entry at the top
  logContainer.insertBefore(logEntry, logContainer.firstChild);

  // Keep only last 50 entries to prevent too much DOM content
  while (logContainer.children.length > 50) {
    logContainer.removeChild(logContainer.lastChild!);
  }
}
